import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import { TCourseUnitType } from '@src/types/course';

import { ArrowDownIcon } from '@src/components/common/icon';
import VideoPlayer from './videoPlayer';

const CollapseTitle = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 16px 0px 16px 24px;
	margin: 0px;
	color: #333333;
	font-size: 18px;
	cursor: pointer;
	background: ${props => `${props.theme.colors.primary}28`};
	border-bottom: 1px solid #FFFFFF;
`;

const TitleContent = styled.div`
	display: flex;
`;

const Chapter = styled.div`
	display: block;
	margin-right: 20px;
	white-space: nowrap;

	@media ${props => props.theme.device.lgDesktop} {
		margin-right: 24px;
	}
`;

const TitleText = styled.div`
	display: -webkit-box;
	overflow: hidden;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	text-overflow: ellipsis;
`;

const ExpandIcon = styled.div`
	display: inline-flex;
	align-items: center;
	margin: 0 24px 0 8px;
	transform: rotate(${props => (props.expand ? 180 : 0)}deg);
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	@media ${props => props.theme.device.tablet} {
		margin-right: 16px;
	}

	@media ${props => props.theme.device.smTablet} {
		margin-right: 16px;
	}

	@media ${props => props.theme.device.mobile} {
		margin-right: 8px;
	}
`;

const CollapseContent = styled.div`
	max-height: ${props => props.maxHeight}px;
	padding-left: 100px;
	overflow: hidden;
	transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	@media ${props => props.theme.device.lgDesktop} {
		padding-left: 104px;
	}

	@media ${props => props.theme.device.mobile} {
		padding-left: 0px;
	}
`;

const UnitTitle = styled.div`
	display: flex;
	padding: 12px 0;
`;

const Video = styled(VideoPlayer)`
	margin: 4px 0 16px;
`;

const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 8px;
	margin-left: 12px;
	background: ${props => props.theme.colors.primary};
	color: #FFFFFF;
	font-size: 12px;
	line-height: 18px;
	white-space: nowrap;
	transform: translateY(-1px);
`;

interface IUnit {
	index: number;
	name: string;
	videoLink: string | undefined;
	previewable: boolean;
	type: TCourseUnitType;
}

interface IChapter {
	chapterIndex: number;
	name: string;
	units: IUnit[];
}

interface ICollapse {
	chapter: IChapter;
	defaultExpand: boolean;
}

const Collapse = ({ chapter, defaultExpand }: ICollapse): JSX.Element => {
	const { t } = useTranslation();
	const contentRef = useRef(null);
	const [isExpand, setIsExpand] = useState(defaultExpand);
	const hasAnyFreeVideo = !!chapter.units.find(unit => unit.previewable);
	const scrollHeight = contentRef.current?.scrollHeight;

	const handleToggleExpand = () => {
		setIsExpand(prev => !prev);
	};

	return (
		<div className="collapse-wrapper">
			<CollapseTitle onClick={handleToggleExpand}>
				<TitleContent>
					<Chapter>{t('course:information:chapter', { chapter: chapter.chapterIndex })}</Chapter>
					<TitleText>
						{chapter.name}
						{hasAnyFreeVideo && !isExpand && <Chip>{t('course:information:free_video')}</Chip>}
					</TitleText>
				</TitleContent>
				<ExpandIcon expand={isExpand}>
					<ArrowDownIcon />
				</ExpandIcon>
			</CollapseTitle>
			<CollapseContent ref={contentRef} maxHeight={isExpand ? scrollHeight : 0}>
				{chapter.units.map(({ type, index, name, previewable, videoLink }) => {
					const itemKey = `${type}_${name}_${index}`;
					return (
						<div className="unit" key={itemKey}>
							<UnitTitle>
								<div className="unit-title__text">
									{name}
									{previewable && <Chip>{t('course:information:free_video')}</Chip>}
								</div>
							</UnitTitle>
							{(previewable && videoLink) && (<Video link={videoLink} />)}
						</div>
					);
				})}
			</CollapseContent>
		</div>
	);
};

export default Collapse;
